import { RefreshCw, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { QualityScoreMeter } from '~/components/common/quality-score-meter';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';

interface Props {
  /** "Before" score on the 0–10 wire scale; undefined when no score is present. */
  beforeScore?: number | undefined;
  /** "After" score on the same 0–10 scale; undefined when no score is present. */
  afterScore?: number | undefined;
  /** Whether the on-demand scoring action should be offered at all — false hides
   *  the button (and, with no score, the whole section). */
  canScore: boolean;
  /** True while the operator's POST/poll is in flight — drives the button spinner. */
  isBusy: boolean;
  /** Body/affordance state: `loading` = reading back an existing score (skeleton,
   *  no button yet); `pending` = scoring in progress; `failed` = retry hint. */
  phase?: 'idle' | 'loading' | 'pending' | 'failed';
  /** Fires scoring — `override` true re-scores an existing score (and is always
   *  sent on a failed retry, per the contract). */
  onScore: (override: boolean) => void;
  /** Outer container classes (padding + border), owned by the host surface so
   *  the section drops its border cleanly when it renders nothing. */
  containerClassName?: string | undefined;
  /** Width wrapper around the meter (e.g. `max-w-3xl`). */
  meterWrapperClassName?: string | undefined;
  /** Frame override passed through to {@link QualityScoreMeter}. */
  meterClassName?: string | undefined;
  /** `data-testid` root; derives `-action-button`, `-meter`, `-loading`, `-scoring`, `-failed`, `-empty`. */
  testId?: string;
}

/**
 * The "Enrichment score" block shared by the product detail page and the product
 * detail modals: renders the before/after {@link QualityScoreMeter} when a score
 * exists, and a Generate / Regenerate / Retry control that drives on-demand
 * scoring. Renders nothing for a product that has neither a score nor the ability
 * to be scored — so unscored products stay hidden exactly as before.
 */
export function EnrichmentScoreSection({
  beforeScore,
  afterScore,
  canScore,
  isBusy,
  phase = 'idle',
  onScore,
  containerClassName,
  meterWrapperClassName,
  meterClassName,
  testId = 'enrichment-score-section',
}: Props) {
  const { t } = useTranslation('common');
  // Both halves are required to draw the comparison meter; a partial score
  // (one side only) reads as "not scored yet" here.
  const pair =
    beforeScore != null && afterScore != null ? { before: beforeScore, after: afterScore } : null;
  const hasScore = pair !== null;
  const isChecking = phase === 'loading';
  const isScoring = phase === 'pending';
  const isFailed = phase === 'failed';

  // Keep hiding the section entirely for a product that can't be scored and has
  // no score to show — matching the prior guard-and-hide behaviour.
  if (!hasScore && !canScore) return null;

  // Hide the action while reading back (we don't yet know Generate vs Regenerate)
  // and while a run we didn't start is still completing (`pending` but not busy) —
  // but keep it (with a spinner) while the operator's own re-score runs.
  const showAction = canScore && !isChecking && !(isScoring && !isBusy);
  const actionLabel = isFailed
    ? t('onDemandScore.retry')
    : hasScore
      ? t('onDemandScore.regenerate')
      : t('onDemandScore.generate');

  return (
    <div data-testid={testId} className={containerClassName}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
          {t('onDemandScore.heading')}
        </h3>
        {showAction && (
          <Button
            variant="outline"
            size="sm"
            // `disabled` yields `cursor-not-allowed` from the Button base styles.
            className="cursor-pointer gap-1.5"
            isLoading={isBusy}
            disabled={isBusy}
            data-testid={`${testId}-action-button`}
            // A failed run always re-scores (override); otherwise override iff a
            // score is already present — an unscored product sends no body.
            onClick={() => {
              onScore(isFailed ? true : hasScore);
            }}
          >
            {!isBusy &&
              (isFailed || hasScore ? (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              ))}
            {actionLabel}
          </Button>
        )}
      </div>

      {pair ? (
        <div className={meterWrapperClassName}>
          <QualityScoreMeter
            {...(meterClassName != null ? { className: meterClassName } : {})}
            beforeScore={pair.before}
            afterScore={pair.after}
            testId={`${testId}-meter`}
          />
        </div>
      ) : isChecking ? (
        <div data-testid={`${testId}-loading`} className={meterWrapperClassName}>
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
      ) : isScoring ? (
        <p data-testid={`${testId}-scoring`} className="text-muted-foreground text-sm">
          {t('onDemandScore.scoring')}
        </p>
      ) : isFailed ? (
        <p data-testid={`${testId}-failed`} className="text-muted-foreground text-sm">
          {t('onDemandScore.failedHint')}
        </p>
      ) : (
        <p data-testid={`${testId}-empty`} className="text-muted-foreground text-sm">
          {t('onDemandScore.emptyHint')}
        </p>
      )}
    </div>
  );
}
